import { TypographyOptions } from '@mui/material/styles/createTypography';
import { ITypographyVariant } from 'types/api';
import { IAppThemeMode } from 'types/theme';

const typographyDefaults: Record<
	ITypographyVariant,
	{
		fontWeight: number;
		fontSize: string;
		lineHeight: number;
		letterSpacing?: string;
		fontFamily?: string;
	}
> = {
	h1: {
		fontWeight: 500,
		fontSize: '2rem', // 32px
		lineHeight: 1.34, // 1.34 * 32px = 42.88px
		letterSpacing: '0.005em' // .5%
	},
	h2: {
		fontWeight: 500,
		fontSize: '1.5rem', // 24px
		lineHeight: 1.24 // 1.24 * 24px = 29.76px
	},
	h3: {
		fontWeight: 500,
		fontSize: '1.25rem', // 20px
		lineHeight: 1.28, // 1.28 * 20px = 25.6px
		letterSpacing: '0.01em' // 1%
	},
	subtitle1: {
		fontWeight: 500,
		fontSize: '1rem', // 16px
		lineHeight: 1.6, // 1.6 * 16px = 25.6px
		letterSpacing: '0.0015em' // .15%
	},
	subtitle2: {
		fontWeight: 500,
		fontSize: '0.875rem', // 14px
		lineHeight: 1.52, // 1.52 * 14px = 21.28px
		letterSpacing: '0.0015em' // .15%
	},
	body1: {
		fontWeight: 500,
		fontSize: '1rem', // 16px
		lineHeight: 1.6, // 1.6 * 16px = 25.6px
		letterSpacing: '0.0015em' // .15%
	},
	body2: {
		fontWeight: 500,
		fontSize: '0.875rem', // 14px
		lineHeight: 1.52, // 1.52 * 14px = 21.28px
		letterSpacing: '0.0015em' // .15%
	},
	button: {
		fontWeight: 500,
		fontSize: '1rem', // 16px
		lineHeight: 1.6, // 1.6 * 16px = 25.6px
		letterSpacing: '0.0015em' // .15%
	}
};

const pxToRem = (px: string): string => {
	const pxNum = parseFloat(px);
	/* istanbul ignore if */
	if (Number.isNaN(pxNum)) {
		return px;
	}

	return `${parseFloat(px) / 16}rem`;
};

export const createThemedTypographies = (
	appThemeMode?: IAppThemeMode
): TypographyOptions => {
	const defaultFont = 'DMSans';
	if (!appThemeMode) {
		return {
			...typographyDefaults,
			fontFamily: defaultFont
		};
	}

	const font1 = appThemeMode.fonts?.font1?.family.join(',') || defaultFont;
	const font2 = appThemeMode.fonts?.font2?.family.join(',') || font1;

	const typographyOptions = {
		...Object.fromEntries(
			Object.entries(typographyDefaults).map(([variant, defaults]) => {
				const variantOverrides =
					appThemeMode.typography?.[variant as ITypographyVariant];

				const variantOptions = {
					...defaults
				};
				if (variantOverrides?.font === 'font2') {
					variantOptions.fontFamily = font2;
				}
				if (variantOverrides?.size) {
					variantOptions.fontSize = pxToRem(variantOverrides.size);
				}
				if (
					variantOverrides?.weight &&
					!Number.isNaN(parseInt(variantOverrides?.weight, 10))
				) {
					variantOptions.fontWeight = parseInt(variantOverrides?.weight, 10);
				}

				return [variant, variantOptions];
			})
		),
		fontFamily: font1
	};

	return typographyOptions;
};
